import React, { useState, useEffect } from 'react'

interface Toast {
  id: string
  type: 'success' | 'error' | 'info' | 'warning'
  title: string
  message?: string
  duration?: number
}

// Simple global toast store
let listeners: Array<(toasts: Toast[]) => void> = []
let toasts: Toast[] = []

function notify(toast: Omit<Toast, 'id'>) {
  const id = Math.random().toString(36).slice(2)
  const newToast: Toast = { ...toast, id }
  toasts = [...toasts, newToast]
  listeners.forEach((l) => l(toasts))

  // Auto-dismiss
  setTimeout(() => {
    dismiss(id)
  }, toast.duration || 4000)
}

function dismiss(id: string) {
  toasts = toasts.filter((t) => t.id !== id)
  listeners.forEach((l) => l(toasts))
}

// Export for use by other components
export const toast = {
  success: (title: string, message?: string) => notify({ type: 'success', title, message }),
  error: (title: string, message?: string) => notify({ type: 'error', title, message }),
  info: (title: string, message?: string) => notify({ type: 'info', title, message }),
  warning: (title: string, message?: string) => notify({ type: 'warning', title, message }),
}

/* ─── Icons ────────────────────────────────────────────────────────────── */

const iconMap: Record<Toast['type'], React.ReactNode> = {
  success: (
    <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
}

const bgMap: Record<Toast['type'], string> = {
  success: 'border-l-success bg-success-light/50',
  error: 'border-l-error bg-error-light/50',
  info: 'border-l-info bg-info-light/50',
  warning: 'border-l-warning bg-warning-light/50',
}

/* ─── Component ────────────────────────────────────────────────────────── */

export const NotificationToast: React.FC = () => {
  const [currentToasts, setCurrentToasts] = useState<Toast[]>([])

  useEffect(() => {
    listeners.push(setCurrentToasts)
    return () => {
      listeners = listeners.filter((l) => l !== setCurrentToasts)
    }
  }, [])

  if (currentToasts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 w-80">
      {currentToasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-start gap-3 px-4 py-3 rounded-lg border-l-4 bg-surface-elevated shadow-float animate-slide-right ${bgMap[t.type]}`}
        >
          <div className="mt-0.5 shrink-0">{iconMap[t.type]}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-txt-primary">{t.title}</p>
            {t.message && (
              <p className="text-xs text-txt-secondary mt-0.5">{t.message}</p>
            )}
          </div>
          <button
            onClick={() => dismiss(t.id)}
            className="shrink-0 p-0.5 rounded text-txt-tertiary hover:text-txt-primary transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  )
}
